import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { type UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { type UnifiedDocument } from 'src/suitability-report/hooks/useAllClientDocuments';

export function useRemoveFamilyMember() {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation<unknown, Error, { familyId: string; clientId: string }>({
    mutationFn: ({ familyId, clientId }) => api.delete(`families/${familyId}/members/${clientId}`),
    onSuccess: (_data, { familyId, clientId }) => {
      // The family's document tabs (generated documents and uploaded files) each
      // aggregate every member's own items. Drop the removed member's items in
      // place so they disappear instantly, regardless of whether those (usually
      // inactive) tabs refetch. Family-owned items have no clientId and are kept.
      queryClient.setQueryData<UnifiedDocument[]>(
        ['families', familyId, 'all-documents'],
        current => current?.filter(document => document.clientId !== clientId),
      );
      queryClient.setQueryData<UploadedFile[]>(
        ['families', familyId, 'data-upload-files'],
        current => current?.filter(file => file.clientId !== clientId),
      );
      queryClient.invalidateQueries({ queryKey: ['families'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const removeFamilyMember = async (familyId: string, clientId: string) => {
    return mutateAsync({ familyId, clientId });
  };

  return {
    removeFamilyMember,
    error,
    isLoading,
    reset,
  };
}
